import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PlusIcon } from "@phosphor-icons/react";
import { Badge } from "@libs/zapi-ui/ds/components/badge";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Skeleton } from "@libs/zapi-ui/ds/components/skeleton";
import { WidgetCardBody } from "@libs/zapi-ui/ds/components/widget-card";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { SCORE_LEVEL_LABEL_KEY, pickCrux, scoreLevel, type TRisk } from "@/zapi-frontend/shared/types/risks";
import { ContextTabs } from "../ProjectLayout/ui/blocks/ContextTabs";
import { WidgetCard, WidgetCardHeader, WidgetTiles } from "../ProjectLayout/ui/WidgetCard";
import { useRisks } from "./useRisks";
import { RiskFormSheet } from "./_RiskFormSheet";
import { AddressRiskSheet } from "./_AddressRiskSheet";
import { RiskGraph } from "./_RiskGraph";

const ROW_GRID = "grid grid-cols-[44px_minmax(0,1fr)_120px] gap-3 @xl:grid-cols-[44px_minmax(0,1fr)_80px_80px_120px]";
const SCORE_COLUMN = "hidden @xl:flex";
const NEXT_LIMIT = 5;
const CRUX_THRESHOLD = 6;

type TTab = "overview" | "table";

export function RisksIsland() {
  const { t } = useTranslation("projectHome");
  const { id } = useParams<{ id: string }>();
  const projectId = id ?? "";
  const { data: risks, isLoading } = useRisks(projectId);

  const [tab, setTab] = useState<TTab>("overview");
  const [formOpen, setFormOpen] = useState(false);
  const [editingRisk, setEditingRisk] = useState<TRisk | null>(null);
  const [addressOpen, setAddressOpen] = useState(false);
  const [addressingRisk, setAddressingRisk] = useState<TRisk | null>(null);

  const cruxIds = useMemo(() => pickCrux(risks ?? []), [risks]);
  const sortedRisks = useMemo(
    () => [...(risks ?? [])].sort((a, b) => b.impact * b.uncertainty - a.impact * a.uncertainty),
    [risks]
  );
  const openRisks = sortedRisks.filter((risk) => risk.status !== "addressed");
  const openCruxRisks = openRisks.filter((risk) => risk.impact >= CRUX_THRESHOLD && risk.uncertainty >= CRUX_THRESHOLD);
  const nextRisks = openRisks.slice(0, NEXT_LIMIT);

  const openAdd = () => {
    setEditingRisk(null);
    setFormOpen(true);
  };
  const openAddress = (risk: TRisk) => {
    setAddressingRisk(risk);
    setAddressOpen(true);
  };
  const openRisk = (risk: TRisk) => {
    if (risk.status === "addressed") return openAddress(risk);
    setEditingRisk(risk);
    setFormOpen(true);
  };

  const overview = (
    <div className="flex flex-col gap-4">
      <WidgetCard>
        <WidgetCardHeader title={t("risks.summaryTitle")} />
        <WidgetCardBody className="min-w-0">
          <WidgetTiles>
            {[
              { label: t("risks.summaryTotal"), value: sortedRisks.length },
              { label: t("risks.summaryOpen"), value: openRisks.length },
              { label: t("risks.summaryCruxOpen"), value: openCruxRisks.length },
              { label: t("risks.summaryAddressed"), value: sortedRisks.length - openRisks.length }
            ].map((tile) => (
              <div key={tile.label} className="flex flex-col gap-1 bg-card px-5 py-4">
                <span className="typo-h3 tabular-nums text-foreground">{tile.value}</span>
                <span className="typo-label-sm text-muted-foreground">{tile.label}</span>
              </div>
            ))}
          </WidgetTiles>
        </WidgetCardBody>
      </WidgetCard>
      <WidgetCard>
        <WidgetCardHeader title={t("risks.graphTitle")} />
        <WidgetCardBody className="min-w-0 p-3 sm:p-4">
          <RiskGraph risks={sortedRisks} cruxIds={cruxIds} onAddress={openAddress} />
        </WidgetCardBody>
      </WidgetCard>
      <WidgetCard>
        <WidgetCardHeader title={t("risks.nextTitle")} />
        <WidgetCardBody className="min-w-0 gap-0 p-0 sm:p-0">
          {nextRisks.length === 0 ? (
            <p className="typo-body-sm px-5 py-6 text-muted-foreground">{t("risks.allAddressed")}</p>
          ) : (
            <ul className="divide-y divide-border">
              {nextRisks.map((risk) => (
                <li key={risk.id} className="flex items-center gap-4 px-5 py-4">
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="typo-label-md truncate text-foreground">{risk.title}</span>
                    <span className="typo-caption truncate text-muted-foreground">{risk.whatMustHoldTrue}</span>
                  </div>
                  {cruxIds.has(risk.id) && <Badge>{t("risks.quadrant.crux")}</Badge>}
                  <Button
                    size="sm"
                    shape="pill"
                    variant="outline"
                    onClick={() => openAddress(risk)}
                    data-testid={`risk-next-address-${risk.id}`}
                  >
                    {t("risks.addressRisk")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </WidgetCardBody>
      </WidgetCard>
    </div>
  );

  const table = (
    <WidgetCard>
      <WidgetCardHeader title={t("risks.tabTable")} />
      <WidgetCardBody className="min-w-0 gap-0 overflow-hidden rounded-b-2xl p-0 sm:p-0">
        <div>
          <div
            className={cn(
              ROW_GRID,
              "border-b border-innoveraZinc-100 p-3 text-[12px] font-medium leading-[11px] text-innoveraZinc-500"
            )}
          >
            <span className="text-center">#</span>
            <span>{t("risks.columnRisk")}</span>
            <span className={SCORE_COLUMN}>{t("risks.columnImpact")}</span>
            <span className={SCORE_COLUMN}>{t("risks.columnUncertainty")}</span>
            <span>{t("risks.columnStatus")}</span>
          </div>
          {sortedRisks.map((risk, index) => {
            const isCrux = cruxIds.has(risk.id);
            return (
              <div
                key={risk.id}
                role="button"
                tabIndex={0}
                onClick={() => openRisk(risk)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openRisk(risk);
                  }
                }}
                className={cn(
                  ROW_GRID,
                  "cursor-pointer border-b border-innoveraZinc-100 bg-white p-3 text-left outline-hidden transition-colors hover:bg-innoveraZinc-50 focus-visible:bg-innoveraZinc-50"
                )}
                data-testid={`risk-row-${risk.id}`}
              >
                <div
                  className={cn(
                    "flex size-11 items-center justify-center text-[14px] font-medium leading-[13px]",
                    isCrux ? "bg-innoveraOrange-400 text-white" : "bg-innoveraZinc-100 text-innoveraZinc-900"
                  )}
                >
                  {index + 1}
                </div>
                <div className="flex min-w-0 flex-col justify-center gap-1">
                  <div className="flex h-5 items-center">
                    <span className="truncate text-[14px] font-medium leading-5 text-black">{risk.title}</span>
                  </div>
                  <span className="truncate text-[12px] leading-[18px] text-innoveraZinc-700">
                    {risk.whatMustHoldTrue}
                  </span>
                  <span className="truncate text-[12px] leading-[18px] text-innoveraZinc-500 @xl:hidden">
                    {t("risks.columnImpact")}: {t(SCORE_LEVEL_LABEL_KEY[scoreLevel(risk.impact)])} ·{" "}
                    {t("risks.columnUncertainty")}: {t(SCORE_LEVEL_LABEL_KEY[scoreLevel(risk.uncertainty)])}
                  </span>
                </div>
                <div className={cn(SCORE_COLUMN, "items-center text-[13px] text-innoveraZinc-700")}>
                  {t(SCORE_LEVEL_LABEL_KEY[scoreLevel(risk.impact)])}
                </div>
                <div className={cn(SCORE_COLUMN, "items-center text-[13px] text-innoveraZinc-700")}>
                  {t(SCORE_LEVEL_LABEL_KEY[scoreLevel(risk.uncertainty)])}
                </div>
                <div className="flex items-center">
                  <Button
                    variant={risk.status === "addressed" ? "outline" : "default"}
                    className="w-full"
                    onClick={(event) => {
                      event.stopPropagation();
                      openAddress(risk);
                    }}
                    data-testid={`risk-status-${risk.id}`}
                  >
                    {risk.status === "addressed" ? t("risks.addressed") : t("risks.addressRisk")}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </WidgetCardBody>
    </WidgetCard>
  );

  return (
    <section data-testid="risks-island" className="mx-auto flex w-full max-w-[960px] flex-col pb-8">
      <div className="mb-2 flex min-h-10 items-center justify-between gap-3">
        <h1 className="typo-h3 min-w-0 truncate text-foreground">{t("risks.title")}</h1>
        <Button shape="pill" onClick={openAdd} data-testid="risks-add-button">
          <PlusIcon data-icon="inline-start" weight="bold" />
          {t("risks.addTitle")}
        </Button>
      </div>
      <ContextTabs
        tabs={[
          { id: "overview", label: t("risks.tabOverview") },
          { id: "table", label: t("risks.tabTable"), count: sortedRisks.length || undefined }
        ]}
        value={tab}
        onValueChange={(next) => setTab(next as TTab)}
        sticky
        className="shrink-0"
      />
      <div className="flex min-w-0 flex-col pt-6">
        {isLoading ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-40 w-full rounded-2xl" />
            <Skeleton className="h-64 w-full rounded-2xl" />
          </div>
        ) : sortedRisks.length === 0 ? (
          <WidgetCard>
            <WidgetCardBody className="min-w-0">
              <p className="typo-body-sm mx-auto max-w-[48ch] py-10 text-center text-muted-foreground">
                {t("risks.empty")}
              </p>
            </WidgetCardBody>
          </WidgetCard>
        ) : tab === "overview" ? (
          overview
        ) : (
          table
        )}
      </div>

      <RiskFormSheet projectId={projectId} open={formOpen} onOpenChange={setFormOpen} risk={editingRisk} />
      <AddressRiskSheet projectId={projectId} open={addressOpen} onOpenChange={setAddressOpen} risk={addressingRisk} />
    </section>
  );
}
